import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import { TextField, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useEffect } from "react";

interface CRRNumberProps {
  crrNumber: string;
  crrNumError: string;
  isDisabled?: boolean;
  onCRRChange: (value: string, error?: string) => void;
}

const tooltipStyles = {
  tooltip: {
    sx: {
      bgcolor: "rgba(0, 0, 0, 0.87)",
      color: "white",
      fontSize: "0.75rem",
      padding: "8px 12px",
      backdropFilter: "blur(2px)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
    },
  },
  arrow: {
    sx: {
      color: "rgba(0, 0, 0, 0.87)",
    },
  },
};

export function validateCRRFormat(crr: string): boolean {
  if (!crr || crr === "-") return true;
  const crrRegex = /^\d{4}\/\d{2}\/\d{4}$/;
  if (!crrRegex.test(crr)) return false;

  const [year, month] = crr.split("/");
  const monthNum = parseInt(month, 10);
  return monthNum >= 1 && monthNum <= 12;
}

function CRRNumberInput(props: CRRNumberProps): JSX.Element {
  const { crrNumber, crrNumError, isDisabled, onCRRChange } = props;

  const isValidFormat = validateCRRFormat(crrNumber);
  const hasError = Boolean(crrNumError || (crrNumber && !isValidFormat));

  const validateAndUpdateCRR = (value: string) => {
    if (value.length > 12) {
      onCRRChange(value, "Can't input more than 12 characters!");
    } else if (value && value !== "-" && !validateCRRFormat(value)) {
      onCRRChange(value, "Invalid CRR format! (YYYY/MM/NNNN)");
    } else {
      onCRRChange(value, "");
    }
  };

  useEffect(() => {
    if (crrNumber && !crrNumError) {
      validateAndUpdateCRR(crrNumber);
    }
  }, [crrNumber]);

  const handleCRRChange = (e) => {
    validateAndUpdateCRR(e.target.value);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <div style={{ position: "relative", minWidth: "200px" }}>
        <TextField
          value={crrNumber}
          onChange={handleCRRChange}
          helperText={crrNumError}
          error={hasError}
          disabled={isDisabled}
          size="small"
          fullWidth
        />
        <div
          style={{
            position: "absolute",
            right: "-35px",
            top: "8px",
            display: "flex",
            alignItems: "center",
          }}
        >
          {isValidFormat ? (
            <CheckCircleIcon color="success" />
          ) : (
            <CancelIcon color="error" />
          )}
        </div>
      </div>
    </div>
  );
}

function CRRNumberField(props: CRRNumberProps): JSX.Element {
  return (
    <Grid item lg={12} md={12} xs={12}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "23px",
            width: "300px",
          }}
        >
          <Typography variant="subtitle1" gutterBottom style={{ margin: 0 }}>
            <b>
              No. CRR / <i>CRR Number</i>
            </b>
          </Typography>
          <Tooltip
            placement="right"
            arrow
            title={
              <span>
                Pastikan nomor CRR benar sebelum mengirim. Jika tidak ada,
                masukkan &apos;-&apos; /{" "}
                <i>
                  Ensure the CRR number is correct before submitting. If
                  unavailable, enter &apos;-&apos;
                </i>
              </span>
            }
            componentsProps={tooltipStyles}
          >
            <InfoIcon
              fontSize="small"
              style={{ marginLeft: "8px", color: "rgba(0, 0, 0, 0.54)" }}
            />
          </Tooltip>
        </div>
        <Typography
          variant="subtitle1"
          gutterBottom
          style={{ width: "15px", margin: 0 }}
        >
          <b>:</b>
        </Typography>
        <CRRNumberInput {...props} />
      </div>
    </Grid>
  );
}

export default CRRNumberField;
